import type { JWTClaims, CurrentUser } from './types';
import type { MenuItem } from '@/core/rbac/types';

/**
 * Decodes the payload segment of a JWT without verifying the signature.
 *
 * Signature verification is the API's responsibility. The client only needs
 * the claims to build local UI state (menus, permissions, display name).
 *
 * Returns null for any malformed input so callers can fall through to logout.
 */
export function decodeJwt(token: string): JWTClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // base64url → base64: replace URL-safe chars and pad to a multiple of 4.
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    return JSON.parse(json) as JWTClaims;
  } catch {
    return null;
  }
}

/**
 * Builds a CurrentUser from decoded JWT claims.
 *
 * The `menu` claim is a JSON-serialised string; parsing failures are caught
 * and default to an empty array so a broken menu payload never crashes auth.
 */
export function parseCurrentUser(claims: JWTClaims): CurrentUser {
  let menu: MenuItem[] = [];
  try {
    const parsed = JSON.parse(claims.menu);
    if (Array.isArray(parsed)) {
      menu = parsed as MenuItem[];
    }
  } catch {
    console.warn('[auth] Failed to parse menu claim — defaulting to empty menu.');
  }

  return {
    userId: claims.userId,
    name: claims.userName,
    email: claims.mail,
    userType: claims.userType,
    sourceTypeId: claims.sourceTypeId,
    language: claims.language,
    menu,
    // The backend encodes these as the string "True" / "False".
    singleMenu: claims.singleMenu === 'True',
    secondToken: claims.secondToken === 'True',
    sourceImage: claims.sourceIMG,
  };
}

/**
 * Returns true if the token's `exp` claim is in the past.
 * Accepts an optional `nowSeconds` override for testability.
 */
export function isExpired(claims: JWTClaims, nowSeconds?: number): boolean {
  const now = nowSeconds ?? Math.floor(Date.now() / 1000);
  return claims.exp < now;
}
